// Datos de todas las landings SEO por categoría/TCG.
// Fuente única: agregar una entrada aquí crea automáticamente la ruta y el sitemap.
import type { GameType } from '@/types/catalog';

export interface CategoryLandingConfig {
  game: GameType;
  gameLabel: string;
  gameCatalogParam: string;
  path: string;
  keyword: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  intro: string[];
  highlights: { title: string; body: string }[];
  faq: { q: string; a: string }[];
  relatedPosts?: { title: string; slug: string }[];
}

export const categoryLandings: CategoryLandingConfig[] = [
  {
    game: 'mtg',
    gameLabel: 'Magic: The Gathering',
    gameCatalogParam: 'mtg',
    path: '/mtg/booster-box',
    keyword: 'booster box',
    title: 'MTG Booster Box — Cajas selladas de Magic: The Gathering | Noobs Gaming Store',
    description:
      'Compra MTG Booster Box selladas en El Salvador: Play, Draft, Set y Collector Booster Box de los últimos sets de Magic: The Gathering. Stock real y envíos a todo el país.',
    h1: 'MTG Booster Box',
    lead:
      'Cajas selladas de Magic: The Gathering listas para abrir, draftear o invertir. Encuentra Play Boosters, Draft Boosters y Set Boosters de los sets más recientes con precios en USD y envíos a todo El Salvador.',
    intro: [
      'Una MTG Booster Box es la forma más eficiente de conseguir cartas nuevas de Magic: The Gathering. Cada caja contiene varios sobres del mismo set, lo que garantiza una buena cantidad de raras, míticas y, dependiendo del producto, foils y cartas de bonus sheet.',
      'En Noobs Gaming Store manejamos Play Booster Box, Draft Booster Box y Set Booster Box de los lanzamientos vigentes de Wizards of the Coast. Todas nuestras cajas vienen selladas de fábrica, con código de barras intacto, ideales para tiendas, organizadores de torneos y coleccionistas.',
      'Si buscas más cartas raras y foils por sobre, revisa también nuestra página dedicada a MTG Collector Booster — es el producto premium pensado para coleccionistas.',
    ],
    highlights: [
      {
        title: 'Selladas de fábrica',
        body: 'Cajas Play, Draft y Set Booster Box originales de Wizards of the Coast, con shrink intacto.',
      },
      {
        title: 'Ideal para draft y limited',
        body: 'Una Draft Booster Box alcanza para un draft completo de 8 jugadores. Perfecto para tu playgroup o tienda.',
      },
      {
        title: 'Envíos a El Salvador',
        body: 'Despachamos a todo el país con seguimiento. Retiro en San Salvador disponible para pedidos coordinados.',
      },
    ],
    faq: [
      {
        q: '¿Cuántos sobres trae una MTG Booster Box?',
        a: 'Una Play o Draft Booster Box trae 36 sobres y una Set Booster Box trae 30 sobres. La Collector Booster Box trae 12 sobres premium.',
      },
      {
        q: '¿Cuál es la diferencia entre Play, Draft y Set Booster?',
        a: 'Draft Booster está optimizada para draftear (15 cartas por sobre, distribución pareja). Set Booster está pensada para abrir por diversión, con más foils y arte. Play Booster combina ambas y es el formato estándar actual de Wizards of the Coast.',
      },
      {
        q: '¿Hacen envíos a todo El Salvador?',
        a: 'Sí. Despachamos a todos los departamentos con costo fijo de envío y seguimiento. También puedes coordinar retiro en San Salvador.',
      },
      {
        q: '¿Las cajas vienen selladas?',
        a: 'Sí, todas nuestras Booster Box de Magic: The Gathering vienen selladas de fábrica con el shrink original de Wizards of the Coast.',
      },
    ],
    relatedPosts: [
      { title: 'Dónde comprar cartas Magic en El Salvador: guía 2026', slug: 'donde-comprar-cartas-magic-el-salvador' },
    ],
  },
  {
    game: 'mtg',
    gameLabel: 'Magic: The Gathering',
    gameCatalogParam: 'mtg',
    path: '/mtg/cartas-singles',
    keyword: 'magic',
    title: 'Cartas Magic: The Gathering sueltas en El Salvador | Noobs Gaming Store',
    description:
      'Compra cartas de Magic: The Gathering en El Salvador. Singles para Commander, Standard y Modern, con precios de referencia, condición verificada y envíos a todo el país.',
    h1: 'Cartas Magic sueltas (singles)',
    lead:
      'Encuentra la carta exacta que te falta para tu mazo de Magic: The Gathering. Vendemos singles por unidad en El Salvador, con condición revisada y precios de referencia actualizados.',
    intro: [
      'Comprar singles es la forma más eficiente de armar un mazo de Magic: The Gathering. En lugar de abrir sobres esperando la carta correcta, adquieres exactamente las piezas que necesitas para tu lista de Commander, Standard, Pioneer o Modern.',
      'En Noobs Gaming Store puedes revisar las cartas disponibles en nuestro catálogo y en el marketplace de la comunidad, donde otros jugadores salvadoreños publican sus binders de venta y cambio con precios de referencia.',
      'Si además quieres abrir producto sellado, revisa nuestras páginas de MTG Booster Box y MTG Collector Booster.',
    ],
    highlights: [
      {
        title: 'Condición verificada',
        body: 'Cada single indica su condición (NM, LP, MP) y su edición, para que sepas exactamente qué recibes.',
      },
      {
        title: 'Precios de referencia',
        body: 'Comparamos con precios de mercado internacionales para que el valor sea justo tanto al comprar como al vender.',
      },
      {
        title: 'Comunidad y binders',
        body: 'Además del inventario de la tienda, puedes buscar cartas en los binders de venta y cambio de otros jugadores en El Salvador.',
      },
    ],
    faq: [
      {
        q: '¿Venden cartas Magic sueltas en El Salvador?',
        a: 'Sí. Vendemos singles de Magic: The Gathering por unidad, tanto en nuestra tienda física en San Salvador como en línea con envío a todo el país.',
      },
      {
        q: '¿Cómo busco una carta específica?',
        a: 'Usa el buscador del catálogo o la herramienta de colecciones y binders, donde puedes buscar por nombre, número de colector y edición.',
      },
      {
        q: '¿Puedo vender o cambiar mis cartas Magic?',
        a: 'La tienda no compra cartas, pero podés publicarlas en tu binder de venta o cambio dentro del marketplace, donde otros jugadores de El Salvador te contactan directamente para hacer tratos entre ustedes.',
      },
      {
        q: '¿Puedo pasar a la tienda a ver las cartas?',
        a: 'Claro. Estamos en Colonia Miramonte, Calle Sisimiles, Plaza Jerez, segundo nivel, locales 115 B-2 y B-3, San Salvador, de miércoles a domingo de 12:00 pm a 7:00 pm.',
      },
    ],
    relatedPosts: [
      { title: 'Dónde comprar cartas Magic en El Salvador: guía 2026', slug: 'donde-comprar-cartas-magic-el-salvador' },
      { title: 'Cómo vender cartas TCG en El Salvador', slug: 'como-vender-cartas-tcg-el-salvador' },
    ],
  },
  {
    game: 'mtg',
    gameLabel: 'Magic: The Gathering',
    gameCatalogParam: 'mtg',
    path: '/mtg/collector-booster',
    keyword: 'collector booster',
    title: 'MTG Collector Booster — Sobres y cajas premium de Magic | Noobs Gaming Store',
    description:
      'MTG Collector Booster en El Salvador: sobres y cajas premium con foils, extended art y borderless de los últimos sets de Magic: The Gathering. Stock real y envíos a todo el país.',
    h1: 'MTG Collector Booster',
    lead:
      'El producto premium de Magic: The Gathering. Cada MTG Collector Booster está lleno de foils, extended art, borderless y tratamientos especiales que no encuentras en sobres regulares. Disponible por sobre o en Collector Booster Box.',
    intro: [
      'El MTG Collector Booster es el sobre pensado para coleccionistas e inversores. A diferencia del Play o Draft Booster, cada Collector Booster trae múltiples raras, foils garantizados y la mayoría de cartas con tratamientos especiales: extended art, borderless, showcase y foil etched.',
      'En Noobs Gaming Store puedes comprar Collector Booster sueltos para probar suerte o la Collector Booster Box completa (12 sobres) para asegurar la mayor cantidad de chase cards del set. Todo sellado de fábrica por Wizards of the Coast.',
      '¿Buscas más volumen al mejor precio por carta? Revisa también nuestra página de MTG Booster Box con Play y Draft Boosters.',
    ],
    highlights: [
      {
        title: 'Foils garantizados',
        body: 'Cada Collector Booster trae varios foils y al menos una rara o mítica con tratamiento especial.',
      },
      {
        title: 'Por sobre o caja',
        body: 'Compra Collector Booster sueltos para probar o la Collector Booster Box de 12 sobres para coleccionistas serios.',
      },
      {
        title: 'Stock real en El Salvador',
        body: 'Inventario verificado, sin pre-órdenes inciertas. Envíos a todo el país con seguimiento.',
      },
    ],
    faq: [
      {
        q: '¿Qué trae un MTG Collector Booster?',
        a: 'Cada sobre trae aproximadamente 15 cartas con foils garantizados, varias raras o míticas, y la mayoría con tratamientos especiales como extended art, borderless o showcase.',
      },
      {
        q: '¿Cuántos sobres trae la Collector Booster Box?',
        a: 'La MTG Collector Booster Box trae 12 sobres Collector Booster del mismo set, sellados de fábrica por Wizards of the Coast.',
      },
      {
        q: '¿Vale la pena un Collector Booster vs un Play Booster?',
        a: 'Si buscas las versiones más coleccionables y foils, sí. Si buscas el mejor precio por carta o draftear, conviene un Play o Draft Booster regular.',
      },
      {
        q: '¿Hacen envíos a El Salvador?',
        a: 'Sí. Despachamos a todos los departamentos con costo fijo y seguimiento. También puedes retirar en San Salvador coordinando previamente.',
      },
    ],
    relatedPosts: [
      { title: 'Dónde comprar cartas Magic en El Salvador: guía 2026', slug: 'donde-comprar-cartas-magic-el-salvador' },
    ],
  },
  {
    game: 'one_piece',
    gameLabel: 'One Piece Card Game',
    gameCatalogParam: 'one_piece',
    path: '/one-piece/booster-box',
    keyword: 'booster box',
    title: 'One Piece Booster Box — Cajas selladas del TCG | Noobs Gaming Store',
    description:
      'Compra One Piece TCG Booster Box selladas en El Salvador: OP-01, OP-09 y los últimos sets de Bandai. Stock real, originales y envíos a todo el país.',
    h1: 'One Piece Booster Box',
    lead:
      'Cajas selladas del One Piece Card Game listas para abrir, jugar o invertir. Manejamos los sets más recientes y los clásicos OP-01 al OP-09 con precios en USD y envíos a todo El Salvador.',
    intro: [
      'Una One Piece TCG Booster Box trae 24 sobres del mismo set, con 12 cartas por sobre y al menos 1 SR (Super Rare) garantizada. Es la forma más eficiente de armar mazos competitivos o cazar leaders, SECs y manga rares para tu colección.',
      'En Noobs Gaming Store manejamos cajas selladas de Bandai de los sets vigentes del One Piece Card Game: Romance Dawn (OP-01), Paramount War, Pillars of Strength, Wings of the Captain, A New Era y los más recientes. Todas las cajas vienen con shrink original.',
      'El One Piece TCG es uno de los TCG con mayor crecimiento en LATAM. Si quieres entrar al meta competitivo o invertir antes que los precios suban, esta es la categoría correcta.',
    ],
    highlights: [
      {
        title: 'Selladas de Bandai',
        body: 'Cajas originales con shrink intacto directo del distribuidor oficial de Bandai.',
      },
      {
        title: '24 sobres por caja',
        body: 'Cada Booster Box trae 24 sobres con 12 cartas. SR garantizada por sobre y chance de SEC o manga rare.',
      },
      {
        title: 'Envíos a El Salvador',
        body: 'Empaque reforzado y seguimiento. Despachamos a todo el país.',
      },
    ],
    faq: [
      {
        q: '¿Cuántos sobres trae una Booster Box de One Piece TCG?',
        a: 'Cada Booster Box del One Piece Card Game trae 24 sobres. Cada sobre contiene 12 cartas con al menos 1 Super Rare (SR) garantizada.',
      },
      {
        q: '¿Qué sets manejan?',
        a: 'Manejamos los sets activos del One Piece TCG incluyendo Romance Dawn (OP-01), Paramount War (OP-02), Pillars of Strength (OP-03), Kingdoms of Intrigue (OP-04), Awakening of the New Era (OP-05) y los más recientes. Revisa el catálogo para stock actual.',
      },
      {
        q: '¿Por qué el One Piece TCG es tan caro?',
        a: 'La demanda en LATAM y EE.UU. supera la producción de Bandai en muchos sets. Las cartas Leader, SEC y manga rares se cotizan alto en el secundario. Comprar caja sellada suele ser la mejor inversión por sobre.',
      },
      {
        q: '¿Las cajas vienen selladas y son originales?',
        a: 'Sí. Todas nuestras Booster Box del One Piece TCG vienen selladas con el shrink original de Bandai. No abrimos ni manipulamos cajas.',
      },
    ],
    relatedPosts: [
      { title: 'Cómo jugar One Piece TCG: reglas básicas y primer mazo', slug: 'como-jugar-one-piece-tcg' },
    ],
  },
  {
    game: 'one_piece',
    gameLabel: 'One Piece Card Game',
    gameCatalogParam: 'one_piece',
    path: '/one-piece/cartas',
    keyword: '',
    title: 'Cartas One Piece TCG — Singles, Leaders y Sellados | Noobs Gaming Store',
    description:
      'Cartas One Piece TCG en El Salvador: singles, leaders, SR, SEC y manga rares de todos los sets. Compra cartas sueltas para completar tu mazo competitivo.',
    h1: 'Cartas One Piece TCG',
    lead:
      'Singles, leaders y rares del One Piece Card Game. Completa tu mazo competitivo o tu colección con cartas sueltas verificadas y precios reales del mercado.',
    intro: [
      'El One Piece Card Game tiene cientos de cartas repartidas entre Leaders, Characters, Events y Stages, con rarezas que van desde Common hasta Secret Rare (SEC) y manga rares. Comprar singles es la forma más eficiente de armar el mazo que quieres sin abrir cajas a la suerte.',
      'En Noobs Gaming Store encuentras singles de todos los sets activos —desde Romance Dawn (OP-01) hasta los más recientes— con stock real y precios actualizados según el meta. Si solo buscas las piezas que te faltan para terminar tu deck, llegaste al lugar correcto.',
      'También manejamos sellados (booster box y starter decks) si prefieres construir tu colección abriendo sobres en lugar de comprar cartas individuales.',
    ],
    highlights: [
      {
        title: 'Singles de todos los sets',
        body: 'Desde OP-01 Romance Dawn hasta los lanzamientos más recientes de Bandai.',
      },
      {
        title: 'Leaders y SR/SEC',
        body: 'Cartas clave para construir mazos del meta actual: leaders, characters competitivos y rares.',
      },
      {
        title: 'Verificadas',
        body: 'Todas las cartas son originales de Bandai. Revisamos condición antes de publicar.',
      },
    ],
    faq: [
      {
        q: '¿Qué tipos de cartas hay en One Piece TCG?',
        a: 'Hay cuatro tipos: Leader (la carta principal de tu mazo), Character (criaturas que atacan y bloquean), Event (efectos puntuales) y Stage (efectos permanentes). Las rarezas van de Common (C) a Secret Rare (SEC) y manga rares.',
      },
      {
        q: '¿Puedo armar un mazo competitivo solo con singles?',
        a: 'Sí, y muchas veces es más barato que abrir cajas. Compra tu Leader, las cartas del meta y los Don necesarios. Revisa nuestra sección de One Piece para los singles más buscados.',
      },
      {
        q: '¿Cómo verifico la condición de una carta?',
        a: 'Cada single tiene su condición listada (NM, LP, MP, HP). En cartas de alto valor mostramos fotos reales del frente y reverso para que veas el estado exacto antes de comprar.',
      },
      {
        q: '¿Hacen envíos a todo El Salvador?',
        a: 'Sí. Despachamos a todos los departamentos con empaque protegido (toploader + sleeve en cartas de valor). Retiro en San Salvador disponible.',
      },
    ],
    relatedPosts: [
      { title: 'Cómo jugar One Piece TCG: reglas básicas y primer mazo', slug: 'como-jugar-one-piece-tcg' },
    ],
  },
  {
    game: 'pokemon',
    gameLabel: 'Pokémon TCG',
    gameCatalogParam: 'pokemon',
    path: '/pokemon/cajas',
    keyword: 'box',
    title: 'Cajas de cartas Pokémon en El Salvador — Booster Box y ETB | Noobs Gaming Store',
    description:
      'Booster Box y Elite Trainer Box de Pokémon TCG selladas en El Salvador. Cajas originales de los últimos sets con envíos a todo el país y precios en USD.',
    h1: 'Cajas de cartas Pokémon',
    lead:
      'Booster Box, Elite Trainer Box y Build & Battle Box de Pokémon TCG, todas selladas de fábrica. La mejor manera de coleccionar, abrir en vivo o invertir en sets completos.',
    intro: [
      'Una caja de cartas Pokémon es la forma más eficiente de coleccionar un set completo. Una Booster Box trae 36 sobres del mismo set, mientras que las Elite Trainer Box (ETB) incluyen 9 sobres más accesorios premium: dados, contadores de daño, carta promo y una caja de almacenamiento.',
      'En Noobs Gaming Store manejamos cajas selladas de los sets vigentes de Pokémon TCG —Scarlet & Violet, sets especiales como 151 y celebraciones aniversario—. Todas vienen con el shrink original de Pokémon Company.',
      'Si solo quieres probar suerte con uno o dos sobres, revisa nuestra página de sobres Pokémon. Para abrir, draftear con amigos o invertir, la caja es la mejor opción.',
    ],
    highlights: [
      {
        title: 'Booster Box selladas',
        body: '36 sobres del mismo set con shrink original de The Pokémon Company.',
      },
      {
        title: 'Elite Trainer Box',
        body: '9 sobres + accesorios premium (dados, contadores, promo, caja de almacenamiento).',
      },
      {
        title: 'Envíos seguros',
        body: 'Empaque reforzado y seguimiento. Despachamos a todo El Salvador.',
      },
    ],
    faq: [
      {
        q: '¿Cuántos sobres trae una Booster Box de Pokémon?',
        a: 'Una Booster Box estándar de Pokémon TCG trae 36 sobres del mismo set. Cada sobre contiene 10 cartas, lo que da 360 cartas en total con varias raras garantizadas.',
      },
      {
        q: '¿Qué incluye una Elite Trainer Box (ETB)?',
        a: 'Una ETB incluye 9 sobres del set, 65 fundas (sleeves), 45 cartas de energía, 6 dados con contadores de daño, 1 dado de moneda, 2 contadores de condición, una guía de jugador, una carta promo y una caja de almacenamiento decorada.',
      },
      {
        q: '¿Las cajas vienen selladas de fábrica?',
        a: 'Sí, todas nuestras Booster Box y Elite Trainer Box vienen selladas con el shrink original de The Pokémon Company. No abrimos ni manipulamos las cajas.',
      },
      {
        q: '¿Conviene una Booster Box o una ETB?',
        a: 'Si buscas máxima cantidad de cartas por dólar, la Booster Box (36 sobres) gana. Si valoras los accesorios premium para jugar (sleeves, contadores, energías) la ETB es mejor relación calidad-precio.',
      },
    ],
    relatedPosts: [
      { title: 'Sobres Pokémon: qué son, cuáles comprar y cómo elegir el mejor', slug: 'sobres-pokemon-guia-comprar' },
    ],
  },
  {
    game: 'pokemon',
    gameLabel: 'Pokémon TCG',
    gameCatalogParam: 'pokemon',
    path: '/pokemon/cartas-singles',
    keyword: '',
    title: 'Cartas Pokémon en El Salvador — Singles sueltos | Noobs Gaming Store',
    description:
      'Compra cartas Pokémon en El Salvador: singles por unidad para tu mazo o colección, con condición revisada, precios de referencia y envíos a todo el país.',
    h1: 'Cartas Pokémon sueltas (singles)',
    lead:
      'Consigue la carta Pokémon exacta que te falta, sin abrir sobres. Vendemos singles por unidad en El Salvador, con condición revisada, edición identificada y precios de referencia actualizados.',
    intro: [
      'Comprar singles de Pokémon TCG es la manera más directa de completar tu colección o terminar un mazo: en vez de abrir sobres esperando suerte, adquieres exactamente la carta, la edición y el número de colector que necesitas.',
      'En Noobs Gaming Store manejamos cartas Pokémon sueltas de sets modernos (Scarlet & Violet y anteriores) y también cartas antiguas de colección. Cada single indica su condición y su set, y todas nuestras cartas son originales de The Pokémon Company.',
      'Además del inventario de la tienda, puedes buscar cartas en el marketplace de la comunidad, donde otros jugadores y coleccionistas salvadoreños publican sus binders de venta y cambio. Si preferís producto sellado, revisa nuestras páginas de sobres Pokémon y cajas Pokémon.',
    ],
    highlights: [
      {
        title: 'Condición y edición claras',
        body: 'Cada carta suelta indica su condición (NM, LP, MP), su set y su número de colector, para que sepas exactamente qué recibes.',
      },
      {
        title: 'Precios de referencia',
        body: 'Comparamos con precios de mercado internacionales para que el valor sea justo tanto al comprar como al vender.',
      },
      {
        title: 'Originales verificadas',
        body: 'Todas las cartas pasan por verificación de autenticidad. Cero falsificaciones, cero proxies.',
      },
    ],
    faq: [
      {
        q: '¿Venden cartas Pokémon sueltas en El Salvador?',
        a: 'Sí. Vendemos singles de Pokémon TCG por unidad, tanto en nuestra tienda física en San Salvador como en línea con envío a todo el país.',
      },
      {
        q: '¿Cómo busco una carta Pokémon específica?',
        a: 'Usa el buscador del catálogo o las herramientas de colecciones y binders: puedes buscar por nombre, número de colector (por ejemplo 32/123) y edición, incluyendo cartas japonesas.',
      },
      {
        q: '¿Cuánto vale una carta Pokémon suelta?',
        a: 'Depende del set, la rareza y la condición. Mostramos precios de referencia de mercado en cada carta, y en el marketplace puedes ver el histórico de ventas de la comunidad.',
      },
      {
        q: '¿Puedo vender o cambiar mis cartas Pokémon?',
        a: 'La tienda no compra cartas, pero podés publicarlas en tu binder de venta o cambio dentro del marketplace, donde otros jugadores y coleccionistas de El Salvador te contactan directamente para hacer tratos entre ustedes.',
      },
      {
        q: '¿Puedo pasar a la tienda a ver las cartas?',
        a: 'Claro. Estamos en Colonia Miramonte, Calle Sisimiles, Plaza Jerez, segundo nivel, locales 115 B-2 y B-3, San Salvador, de miércoles a domingo de 12:00 pm a 7:00 pm.',
      },
    ],
    relatedPosts: [
      { title: 'Precio de cartas Pokémon: cómo saber cuánto vale tu colección', slug: 'precio-cartas-pokemon-guia' },
      { title: 'Cómo vender cartas TCG en El Salvador', slug: 'como-vender-cartas-tcg-el-salvador' },
    ],
  },
  {

    game: 'pokemon',
    gameLabel: 'Pokémon TCG',
    gameCatalogParam: 'pokemon',
    path: '/pokemon/cartas-originales',
    keyword: '',
    title: 'Cartas Pokémon Originales — 100% Auténticas | Noobs Gaming Store',
    description:
      'Compra cartas Pokémon originales y verificadas en El Salvador. Singles, sobres y cajas auténticas de The Pokémon Company, con garantía de autenticidad y envíos al país.',
    h1: 'Cartas Pokémon originales',
    lead:
      'Todas las cartas Pokémon que vendemos son 100% originales y verificadas. Cero falsificaciones, cero proxies — solo cartas auténticas de The Pokémon Company directo a tu colección.',
    intro: [
      'El mercado de cartas Pokémon en LATAM está lleno de falsificaciones cada vez más sofisticadas. En Noobs Gaming Store cada single, sobre y caja pasa por verificación de autenticidad antes de ser publicado: revisamos textura, brillo holográfico, tipografía, peso y, en cartas de alto valor, la luz negra.',
      'Trabajamos con distribuidores oficiales y coleccionistas de la comunidad. Nuestros sellados (sobres, booster box, ETB) vienen directo de fábrica con el sello holográfico original de The Pokémon Company.',
      'Si compras una carta y descubres que no es original, te devolvemos el 100% del dinero. Punto. Por eso somos la tienda de confianza para coleccionistas serios en El Salvador.',
    ],
    highlights: [
      {
        title: 'Verificación obligatoria',
        body: 'Cada single de alto valor pasa por revisión visual y de luz negra antes de publicarse.',
      },
      {
        title: 'Sellados de distribuidor',
        body: 'Sobres y cajas con shrink original de The Pokémon Company, sin manipular.',
      },
      {
        title: 'Garantía de autenticidad',
        body: 'Si una carta no es original, devolvemos el 100% del dinero. Sin preguntas.',
      },
    ],
    faq: [
      {
        q: '¿Cómo verifican que las cartas son originales?',
        a: 'Revisamos textura, alineación de capas, brillo holográfico, tipografía y peso. Para cartas valoradas en más de USD 30 también usamos luz UV: las cartas originales brillan azul bajo luz negra, las falsas no.',
      },
      {
        q: '¿Qué pasa si recibo una carta falsa?',
        a: 'Te reembolsamos el 100% del valor pagado, incluyendo envío. Solo necesitas reportarlo dentro de los 7 días posteriores a recibir tu pedido con fotos de la carta.',
      },
      {
        q: '¿Cómo distingo una carta Pokémon original de una falsa?',
        a: 'Las señales más claras: la carta original tiene textura, los colores son nítidos, la fuente del texto es uniforme, el borde negro es perfectamente simétrico y las cartas holográficas tienen brillo con patrón definido. Las falsas suelen ser más delgadas, brillan parejo (sin patrón) y tienen colores apagados.',
      },
      {
        q: '¿Venden proxies o reprints no oficiales?',
        a: 'No. Solo vendemos cartas Pokémon originales producidas por The Pokémon Company. Cero proxies, cero reprints no oficiales.',
      },
    ],
    relatedPosts: [
      { title: 'Precio de cartas Pokémon: cómo saber cuánto vale tu colección', slug: 'precio-cartas-pokemon-guia' },
    ],
  },
  {
    game: 'pokemon',
    gameLabel: 'Pokémon TCG',
    gameCatalogParam: 'pokemon',
    path: '/pokemon/sobres',
    keyword: 'sobre',
    title: 'Sobres Pokémon TCG en El Salvador — Booster Packs sellados | Noobs Gaming Store',
    description:
      'Compra sobres de Pokémon TCG sellados en El Salvador: booster packs de los últimos sets, originales y con envíos a todo el país. Precios en USD y stock real.',
    h1: 'Sobres Pokémon',
    lead:
      'Booster packs sellados de Pokémon TCG listos para abrir. Encuentra sobres de los sets más recientes —Scarlet & Violet, 151, Twilight Masquerade y más— con precios en USD y envíos a todo El Salvador.',
    intro: [
      'Los sobres Pokémon (booster packs) son la forma clásica de empezar o ampliar tu colección. Cada sobre contiene 10 cartas con la oportunidad de obtener raras holográficas, full art, alt art y secret rares dependiendo del set.',
      'En Noobs Gaming Store manejamos sobres originales sellados de los sets vigentes de Pokémon TCG, incluyendo las expansiones más populares de Scarlet & Violet. Todos nuestros booster packs vienen directamente de distribuidor oficial.',
      'Si buscas mejor relación cantidad/precio, revisa nuestras cajas de cartas Pokémon: una booster box trae 36 sobres del mismo set, ideal para abrir en stream o armar colección rápido.',
    ],
    highlights: [
      {
        title: 'Originales y sellados',
        body: 'Sobres directos de distribuidor, con el sello holográfico oficial de Pokémon.',
      },
      {
        title: 'Sets recientes',
        body: 'Booster packs de los sets vigentes de Scarlet & Violet y especiales como 151.',
      },
      {
        title: 'Envíos a El Salvador',
        body: 'Despachamos a todos los departamentos con seguimiento. Retiro en San Salvador disponible.',
      },
    ],
    faq: [
      {
        q: '¿Cuántas cartas trae un sobre Pokémon?',
        a: 'Cada booster pack de Pokémon TCG trae 10 cartas: 5 comunes, 3 poco comunes y al menos 1 carta rara o mejor (rara holográfica, ultra rara, full art, etc.).',
      },
      {
        q: '¿Cómo sé si un sobre Pokémon es original?',
        a: 'Los sobres originales traen el sello holográfico de Pokémon TCG y peso uniforme. Todos nuestros sobres vienen sellados de fábrica y son 100% originales — no vendemos cartas ni sobres falsos.',
      },
      {
        q: '¿Conviene comprar sobres sueltos o una caja?',
        a: 'Si solo quieres probar suerte o cazar una carta específica, los sobres sueltos son ideales. Si planeas abrir varios del mismo set, una caja (36 sobres) suele tener mejor precio por sobre.',
      },
      {
        q: '¿Hacen envíos a todo El Salvador?',
        a: 'Sí. Despachamos a todos los departamentos con costo fijo de envío y seguimiento. También puedes coordinar retiro en San Salvador.',
      },
    ],
    relatedPosts: [
      { title: 'Sobres Pokémon: qué son, cuáles comprar y cómo elegir el mejor', slug: 'sobres-pokemon-guia-comprar' },
      { title: 'Precio de cartas Pokémon: cómo saber cuánto vale tu colección', slug: 'precio-cartas-pokemon-guia' },
    ],
  },
  {
    game: 'yugioh',
    gameLabel: 'Yu-Gi-Oh! TCG',
    gameCatalogParam: 'yugioh',
    path: '/yugioh/booster-box',
    keyword: 'booster box',
    title: 'Yu-Gi-Oh! Booster Box — Cajas selladas | Noobs Gaming Store',
    description:
      'Compra Yu-Gi-Oh! Booster Box selladas en El Salvador. Sets recientes con envíos a todo el país.',
    h1: 'Yu-Gi-Oh! Booster Box',
    lead:
      'Cajas selladas de Yu-Gi-Oh! con los sets más recientes. Stock real y envíos a todo El Salvador.',
    intro: [
      'Las Booster Box de Yu-Gi-Oh! son la forma más eficiente de abrir cantidad y armar tu colección o mazo competitivo del meta actual.',
      'En Noobs Gaming Store manejamos cajas selladas de los sets recientes de Yu-Gi-Oh! TCG, directo del distribuidor oficial.',
      'Si quieres entrar al juego o expandir tu colección, una Booster Box es el mejor punto de partida.',
    ],
    highlights: [
      { title: 'Selladas de fábrica', body: 'Cajas con shrink intacto del distribuidor oficial.' },
      { title: 'Sets actuales', body: 'Los lanzamientos más recientes del meta competitivo.' },
      { title: 'Envío nacional', body: 'Despacho a todo El Salvador en 24–72h hábiles.' },
    ],
    faq: [
      {
        q: '¿Cuántos sobres trae una Booster Box de Yu-Gi-Oh!?',
        a: 'Depende del set; las Core Boxes traen 24 sobres de 9 cartas cada uno. Special Boxes y Tin Boxes varían — revisa la descripción del producto.',
      },
      {
        q: '¿Qué set de Yu-Gi-Oh! me recomiendan para empezar?',
        a: 'Los Starter Decks y Structure Decks son el mejor punto de entrada. Si ya conoces el juego, abre el set principal del meta vigente.',
      },
    ],
    relatedPosts: [],
  },
  {
    game: 'yugioh',
    gameLabel: 'Yu-Gi-Oh! TCG',
    gameCatalogParam: 'yugioh',
    path: '/yugioh/structure-decks',
    keyword: 'structure deck',
    title: 'Structure Decks Yu-Gi-Oh! — Mazos listos | Noobs Gaming Store',
    description:
      'Structure Decks oficiales de Yu-Gi-Oh! TCG listos para jugar. Mazos preconstruidos con envíos a todo El Salvador.',
    h1: 'Yu-Gi-Oh! Structure Decks',
    lead:
      'Structure Decks preconstruidos de Yu-Gi-Oh! listos para abrir y jugar. Ideales para entrar al juego o reforzar un arquetipo.',
    intro: [
      'Los Structure Decks son mazos preconstruidos pensados para que cualquier jugador entre directo con una lista funcional alrededor de un arquetipo específico.',
      'En Noobs Gaming Store manejamos los Structure Decks oficiales de Konami con envíos a todo El Salvador.',
      'Para empezar a jugar Yu-Gi-Oh! te recomendamos un Structure Deck reciente del meta o uno temático que te guste.',
    ],
    highlights: [
      { title: 'Listos para jugar', body: 'Mazos balanceados que funcionan sacándolos de la caja.' },
      { title: 'Arquetipo enfocado', body: 'Cada deck gira en torno a una mecánica o tipo de monstruo.' },
      { title: 'Punto de entrada', body: 'Ideal para nuevos jugadores o como base para construir tu lista.' },
    ],
    faq: [
      {
        q: '¿Cuántas cartas trae un Structure Deck de Yu-Gi-Oh!?',
        a: 'Un Structure Deck oficial trae típicamente 43 cartas (40 main + 3 extra) preconstruidas para jugar de inmediato.',
      },
      {
        q: '¿Puedo modificar un Structure Deck?',
        a: 'Sí. Los Structure Decks son legales tal cual salen, pero la mayoría de jugadores los modifican agregando staples y cartas del meta para hacerlos más competitivos.',
      },
    ],
    relatedPosts: [],
  },
];

export const categoryLandingByPath = new Map(categoryLandings.map((c) => [c.path, c]));
